import {
  Box,
  Button,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../../app/hooks'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { AppDispatch } from '../../../../app/store'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  GET_OPD_SYSTEMIC_ENDPOINT,
  POST_OPD_SYSTEMIC_ENDPOINT,
} from '../../../../utils/Endpoints'
import {
  opdFormDataSaveApi,
  opdFormDataFetchApi,
} from '../../../../services/OpdFormsSTTService'
import { toastMessages, CLINICAL_TEXT_FIELDS } from '../constants'
import {
  selectClinicalExamination,
  isInitialLoadForOPDForms,
  setLoading,
  updateClinicalExamination,
  isSystemicExamOpdFormSubmitted,
  setIsSystemicExamOpdFormSubmit,
} from '../opdFormsSlice'

const styles = (theme: Theme) => ({
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      minWidth: '250px',
      padding: '4px',
    },
  },

  fieldLabel: {
    textAlign: 'right',
    textWrap: 'wrap',
    fontSize: '12px',
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '152px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '250px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '69%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '62%',
    },
  },
})

const ClinicalExamination = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const visitType = 'dental'
  const { id } = useParams()
  const clinicalExamination: any = useAppSelector(selectClinicalExamination)
  const dispatch = useDispatch<AppDispatch>()
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isSystemicExamOpdFormSubmitted)

  const initialValues = {
    intraOralExamination: clinicalExamination?.intraOralExamination || '',
    swelling: clinicalExamination?.swelling || '',
    lymphnodeEnlargement: clinicalExamination?.lymphnodeEnlargement || '',
    sinusOpening: clinicalExamination?.sinusOpening || '',
  }

  const validationSchema = Yup?.object({
    intraOralExamination: Yup?.string(),
    swelling: Yup?.string(),
    lymphnodeEnlargement: Yup?.string(),
    sinusOpening: Yup?.string(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleClinicalExaminationSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleClinicalExaminationSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_SYSTEMIC_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsSystemicExamOpdFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }
    setSubmitting(false)
  }

  const getClinicalExamination = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getClinicalExaminationUrl = `${GET_OPD_SYSTEMIC_ENDPOINT}${id}&visitType=opd/${visitType}`

    const response = await opdFormDataFetchApi(getClinicalExaminationUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateClinicalExamination({
          intraOralExamination: result?.intraOralExamination,
          swelling: result?.swelling,
          lymphnodeEnlargement: result?.lymphondeEnlargement,
          sinusOpening: result?.sinusOpening,
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }
    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getClinicalExamination()
  }, [getClinicalExamination])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      gap={1}
      width={'fit-content'}
      className={classes?.customFontBox}
    >
      {CLINICAL_TEXT_FIELDS?.map(({ name, label, placeholder }) => (
        <Box
          display={'flex'}
          justifyContent={'start'}
          alignItems={'center'}
          width={'100%'}
          key={name}
          flexWrap={'wrap'}
          gap={1}
        >
          <Typography className={classes?.fieldLabel}>{label}: </Typography>

          <TextField
            multiline
            rows={3}
            variant='outlined'
            name={name}
            placeholder={placeholder}
            value={formik?.values[name as keyof typeof formik.values] || ''}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={
              formik?.touched[name as keyof typeof formik.values] &&
              Boolean(formik?.errors[name as keyof typeof formik.errors])
            }
            className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
          />
        </Box>
      ))}

      <Box
        display={'flex'}
        justifyContent={'flex-end'}
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          className={classes?.submitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default ClinicalExamination
